import { useState, useImperativeHandle, forwardRef } from 'react';
import { useCustomerInvoiceAttachments } from '../hooks/useCustomerInvoiceAttachments';
import { deleteAttachment } from '../services/customerInvoiceAttachmentService';
import { CustomerInvoiceAttachmentGrid } from './CustomerInvoiceAttachmentGrid';
import { CustomerInvoiceAttachmentLightbox } from './CustomerInvoiceAttachmentLightbox';
import type { CustomerInvoiceAttachmentView } from '../types';

export interface CustomerInvoiceAttachmentsSectionRef {
  refetch: () => Promise<void>;
}

interface CustomerInvoiceAttachmentsSectionProps {
  projectId: string;
  onRefetchReady?: (refetch: () => Promise<void>) => void;
}

export const CustomerInvoiceAttachmentsSection = forwardRef<
  CustomerInvoiceAttachmentsSectionRef,
  CustomerInvoiceAttachmentsSectionProps
>(function CustomerInvoiceAttachmentsSection({ projectId, onRefetchReady }, ref) {
  const { attachments, isLoading, error, refetch } = useCustomerInvoiceAttachments({
    projectId,
  });

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Expose refetch method via ref
  useImperativeHandle(ref, () => ({
    refetch,
  }));

  // Also notify parent of refetch function
  if (onRefetchReady) {
    onRefetchReady(refetch);
  }

  const selectedAttachment = selectedIndex !== null
    ? attachments[selectedIndex]
    : null;

  const handleAttachmentClick = (attachment: CustomerInvoiceAttachmentView) => {
    const index = attachments.findIndex((a) => a.id === attachment.id);
    setSelectedIndex(index);
  };

  const handleCloseLightbox = () => {
    setSelectedIndex(null);
  };

  const handlePrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedIndex !== null && selectedIndex < attachments.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handleDelete = async (attachment: CustomerInvoiceAttachmentView) => {
    setIsDeleting(true);
    try {
      await deleteAttachment(attachment.id, attachment.filePath);
      await refetch();
    } catch (err) {
      console.error('Error deleting attachment:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdate = async () => {
    // Refetch to get updated data
    await refetch();
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        Error loading customer documents: {error}
      </div>
    );
  }

  return (
    <>
      <CustomerInvoiceAttachmentGrid
        attachments={attachments}
        onAttachmentClick={handleAttachmentClick}
        onDelete={handleDelete}
        showDeleteButton
        isLoading={isLoading}
        emptyMessage="No customer documents yet"
      />

      {/* Lightbox */}
      {selectedAttachment && (
        <CustomerInvoiceAttachmentLightbox
          attachment={selectedAttachment}
          onClose={handleCloseLightbox}
          onPrevious={handlePrevious}
          onNext={handleNext}
          hasPrevious={selectedIndex !== null && selectedIndex > 0}
          hasNext={selectedIndex !== null && selectedIndex < attachments.length - 1}
          onDelete={handleDelete}
          onUpdate={handleUpdate}
          showDeleteButton={!isDeleting}
          showEditButton
        />
      )}
    </>
  );
});
